import type { CrudAdapter } from '@botto/core';
import { useCrudDataSource } from '@botto/core';
import { smsClient } from '../client';
import type { StreamGroup, StreamGroupCreate, StreamGroupUpdate } from '../types';

export function createGroupsAdapter(
  streamId: number,
): CrudAdapter<StreamGroup, StreamGroupCreate, StreamGroupUpdate> {
  return {
    async list({ offset, limit, sortName, sortDirection, filter }) {
      const directionId = filter?.direction_id as number | undefined;
      const { data, error } = await smsClient.GET('/streams/{stream_id}/groups/', {
        params: {
          path: { stream_id: streamId },
          query: {
            offset,
            limit,
            'sort.name': sortName ?? 'id',
            'sort.direction': (sortDirection ?? 'asc') as 'asc' | 'desc',
            ...(directionId != null ? { direction_id: directionId } : {}),
          },
        },
      });
      if (error) throw error;
      return { items: data?.items ?? [], total: data?.total ?? 0 };
    },
    async create(payload) {
      const body: StreamGroupCreate = {
        ...payload,
        stream_id: streamId,
      };
      const { data, error } = await smsClient.POST('/streams/{stream_id}/groups/', {
        params: { path: { stream_id: streamId } },
        body,
      });
      if (error) throw error;
      return { id: data?.id ?? 0 };
    },
    async update(payload) {
      const { data, error } = await smsClient.PUT('/streams/{stream_id}/groups/', {
        params: { path: { stream_id: streamId } },
        body: { ...payload, stream_id: streamId },
      });
      if (error) throw error;
      return (data ?? payload) as StreamGroup;
    },
    async byKey(id) {
      const { data, error } = await smsClient.GET('/streams/{stream_id}/groups/{item_id}', {
        params: { path: { stream_id: streamId, item_id: Number(id) } },
      });
      if (error) throw error;
      return data as StreamGroup;
    },
    async remove(id) {
      const { error } = await smsClient.DELETE('/streams/{stream_id}/groups/{item_id}', {
        params: { path: { stream_id: streamId, item_id: Number(id) } },
      });
      if (error) throw error;
    },
  };
}

export function createGroupsStore(streamId: number) {
  return useCrudDataSource(createGroupsAdapter(streamId), { defaultPageSize: 1000 });
}
